import LexusIcon from '@assets/icons/lexusInfo.svg'
import ToyotaIcon from '@assets/icons/toyotaInfo.svg'
import { useBrand } from '@contexts/index'
import { tracking } from '@utils/helpers/index-client'
import { customEvent } from '@utils/helpers/tracking'
import { useState } from 'react'
import Modal from 'react-modal'
import { upperCaseFirst } from '@utils/helpers/stringHelpers'

import { CheckboxInput } from '../baseInputs'

const TooltipIcon = ({ brand, ...rest }) => {
  if (brand === 'lexus') {
    return <LexusIcon {...rest} />
  }

  return <ToyotaIcon {...rest} />
}

const MarketingConsent = ({ ...rest }) => {
  const brand = useBrand()
  const [personalisedModalIsOpen, personalisedSetIsOpen] = useState(false)
  const [unsubscribeModalIsOpen, unsubscribeSetIsOpen] = useState(false)
  Modal.setAppElement('#__next')

  function closePersonalisedModal() {
    personalisedSetIsOpen(false)
    customEvent({
      name: 'componentevent',
      componentName: tracking.componentName(),
      componentGroup: tracking.componentGroup(),
      action: 'consent-close-modal',
      label: 'personalisation',
    })
  }
  function openUnsubscribeModal() {
    unsubscribeSetIsOpen(true)
  }
  function closeUnsubscribeModal() {
    unsubscribeSetIsOpen(false)
    customEvent({
      name: 'componentevent',
      componentName: tracking.componentName(),
      componentGroup: tracking.componentGroup(),
      action: 'consent-close-modal',
      label: 'unsubscribe',
    })
  }

  const privacyLink =
    brand === 'lexus'
      ? 'https://www.lexus.co.uk/customer-data/privacy-policy'
      : 'https://www.toyota.co.uk/footer/privacy-policy'

  const unsubscribeLink =
    brand === 'lexus'
      ? 'https://forms2.lexus.co.uk/unsubscribe'
      : 'https://forms.toyota.co.uk/unsubscribe'

  return (
    <div className="mt-8 text-base-text">
      <p className="mb-8">
        We&apos;d like to keep you updated on our latest news, offers and
        events. We will send these communications by email, phone, SMS or post.
        Be assured that {upperCaseFirst(brand)} will only share your personal
        information with companies that are an integral part of fulfilling the
        services we deliver. If you would like to find out more about how we
        process your data please visit our{' '}
        <a
          className="underline"
          href={privacyLink}
          target={'_blank'}
          rel={'noopener noreferrer'}
          data-gt-name="ctaevent"
          data-gt-action="consent-click-link"
          data-gt-label={privacyLink}
        >
          privacy notice
        </a>{' '}
        for details.
      </p>
      <h5 className={`${brand === 'lexus' ? 'text-lg' : 'font-medium '} mb-3`}>
        Yes, I&apos;d like to receive:
      </h5>
      <CheckboxInput name={'consent.user'} {...rest}>
        Latest news, offers and events from {upperCaseFirst(brand)}
      </CheckboxInput>
      <p className="mt-2">
        I understand that I can{' '}
        <a
          className="underline"
          href={unsubscribeLink}
          target={'_blank'}
          rel={'noopener noreferrer'}
          data-gt-name="ctaevent"
          data-gt-action="consent-click-link"
          data-gt-label={unsubscribeLink}
        >
          unsubscribe
        </a>{' '}
        at any{' '}
        <span className="whitespace-nowrap">
          time.
          <TooltipIcon
            brand={brand}
            className="inline-block ml-2 h-[17px]  mt-[-5px]  cursor-pointer text-base-text"
            data-tip
            data-for="unsubscribeTooltip"
            data-gt-name="componentevent"
            data-gt-action="consent-open-modal"
            data-gt-label="unsubscribe"
            onClick={(e) => {
              e.preventDefault()
              openUnsubscribeModal()
            }}
          />
        </span>
      </p>
      <Modal
        className={`${
          brand === 'toyota' ? 'rounded' : ''
        } absolute bg-base text-white top-1/2 left-1/2 right-auto bottom-auto mr-[-50%] -translate-y-1/2 -translate-x-1/2 max-w-[90%] md:max-w-screen-sm max-h-[60%] p-8 overflow-y-auto w-full focus:border-opacity/0 outline-none`}
        overlayClassName="fixed top-0 left-0 right-0 bottom-0 z-20 bg-[#FFF]/75"
        id="personalisedModal"
        isOpen={personalisedModalIsOpen}
        onRequestClose={closePersonalisedModal}
      >
        <button
          className="absolute top-3 right-4 text-3xl leading-none"
          onClick={closePersonalisedModal}
        >
          &times;
        </button>
        <h4 className="font-medium mb-3">Our Marketing Communications</h4>
        <p>
          We want to make our communications as relevant as possible to your
          interests. To do that we will use data we hold about you to create an
          individual customer profile. This profile is created using data you
          have provided to us, for example, contact details, preferences,
          vehicle information, and online activity. Our policy at{' '}
          {upperCaseFirst(brand)} (GB) plc is to never share your personal
          information with companies unless they have a direct connection with
          us and help us to deliver the best possible products, services and
          offers based on your requirements and preference. You can unsubscribe
          at any time. Find more information on how we handle your data in our{' '}
          <a
            className="underline"
            href={privacyLink}
            target={'_blank'}
            rel={'noopener noreferrer'}
            data-gt-name="ctaevent"
            data-gt-action="consent-click-link"
            data-gt-label={privacyLink}
          >
            privacy notice
          </a>
          .
        </p>
      </Modal>
      <Modal
        className={`${
          brand === 'toyota' ? 'rounded' : ''
        } absolute bg-base text-white top-1/2 left-1/2 right-auto bottom-auto mr-[-50%] -translate-y-1/2 -translate-x-1/2 max-w-[90%] md:max-w-screen-sm max-h-[50%] p-8 overflow-y-auto w-full focus:border-opacity/0 outline-none`}
        overlayClassName="fixed top-0 left-0 right-0 bottom-0 z-20 bg-[#FFF]/75"
        id="unsubscribeModal"
        isOpen={unsubscribeModalIsOpen}
        onRequestClose={closeUnsubscribeModal}
      >
        <button
          className="absolute top-3 right-4 text-3xl leading-none"
          onClick={closeUnsubscribeModal}
        >
          &times;
        </button>
        <p className="mb-3">To amend or withdraw your consent at any time:</p>
        <ul className="list-disc pl-5">
          <li className="mb-1">
            <a
              className="underline"
              href={unsubscribeLink}
              target={'_blank'}
              rel={'noopener noreferrer'}
              data-gt-name="ctaevent"
              data-gt-action="consent-click-link"
              data-gt-label={unsubscribeLink}
            >
              Unsubscribe
            </a>{' '}
            here
          </li>
          <li className="mb-1">
            Log into your My{upperCaseFirst(brand)} account and update your
            preferences
          </li>
          <li className="mb-1">
            Click on the{' '}
            <a
              className="underline"
              href={unsubscribeLink}
              target={'_blank'}
              rel={'noopener noreferrer'}
              data-gt-name="ctaevent"
              data-gt-action="consent-click-link"
              data-gt-label={unsubscribeLink}
            >
              unsubscribe
            </a>{' '}
            link in any future communication we send you
          </li>
        </ul>
      </Modal>
    </div>
  )
}

export default MarketingConsent
